import { Field, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { Control, Controller } from 'react-hook-form'
import { t } from '../../../i18n/i18n'
import { RTFOutput } from '../outputs/RTFOutput'
import { EditorProvider } from '@tiptap/react'
import { MenuBar } from './MenuBar'
import { rtfExtensions } from './extensions/rtfExtensions'
import { sanitizePastedHtml } from './sanitizePastedHtml'
import { rtfListStyles } from '../rtf/rtfListStyles'
import './reset.css'
import { useEffect, useRef } from 'react'
type ControlledRTFInputProps = {
   name: any
   control: Control<any>
   label: string
   watchedValue: string
   isDisabled?: boolean
   required?: boolean
   editorClassName?: string
}
const useStyles = makeStyles({
   rtf: {
      border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
      borderRadius: tokens.borderRadiusMedium,
      fontSize: tokens.fontSizeBase300,
   },
   rtfContainer: {
      backgroundColor: tokens.colorNeutralBackground1,
      borderBottomColor: tokens.colorNeutralStrokeAccessible,
   },
   margin: {
      marginBottom: tokens.spacingVerticalXL,
   },
   editor: {
      padding: `0 ${tokens.spacingHorizontalL}`,
      minHeight: '200px',
      wordBreak: 'break-word',
      // TipTap serialises list items as <li><p>…</p></li>. Without this the default
      // paragraph margins make every list item a full blank line tall.
      '& li > p': { marginTop: 0, marginBottom: 0 },
      '& li > ul': { marginTop: 0, marginBottom: 0 },
      '& li > ol': { marginTop: 0, marginBottom: 0 },
      // Word's multilevel list markers and indent steps, shared verbatim with RTFOutput and
      // with robust-web so the author sees exactly what the reader will see. Every depth
      // selector in there ends in `:not([type])`, and must keep doing so: `type="a"` is only
      // a presentational hint, so any author rule that matched a typed list would silently
      // disable the lettered lists of #4735.
      ...rtfListStyles,
   },
   disabled: {
      padding: `0 ${tokens.spacingHorizontalL}`,
      minHeight: '200px',
      wordBreak: 'break-word',
   },
   label: {
      marginTop: tokens.spacingVerticalXXS,
      marginBottom: tokens.spacingVerticalXS,
   },
})

export const ControlledRTFInput = ({ name, control, label, watchedValue, isDisabled, required, editorClassName }: ControlledRTFInputProps) => {
   const classes = useStyles()

   const editorRef = useRef(null)

   useEffect(() => {
      if (editorRef.current && editorRef.current.getHTML() !== watchedValue) {
         editorRef.current.commands.setContent(watchedValue) // Set content when watchedValue changes
      }
   }, [watchedValue])
   return (
      <Controller
         name={name}
         control={control}
         rules={{ required: required ? t('RequiredField') : false }}
         render={({ field: { value, onChange } }) => (
            <Field label={label} required={required} className={mergeClasses(classes.margin)}>
               {isDisabled ? (
                  <RTFOutput text={value} className={mergeClasses(classes.disabled, classes.rtf)} />
               ) : (
                  <div className={mergeClasses(classes.rtfContainer, classes.rtf, editorClassName ?? '')}>
                     <EditorProvider
                        editorProps={{
                           attributes: { class: mergeClasses(classes.editor, editorClassName ?? '') },
                           // Pasted markup is cut down to what the toolbar itself can produce, so that
                           // nothing arrives looking bold while being out of the buttons' reach (#6465).
                           transformPastedHTML: sanitizePastedHtml,
                        }}
                        slotBefore={<MenuBar />}
                        extensions={rtfExtensions}
                        content={value}
                        onCreate={({ editor }) => {
                           editorRef.current = editor // Save editor instance to ref
                        }}
                        onUpdate={(props) => onChange(props.editor.getHTML())}
                     />
                  </div>
               )}
            </Field>
         )}
      />
   )
}
